import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddBedroomDialog from "./AddBedroomDialog";
import { listActiveCampuses } from "../api/campusesApi";
import EventIcon from "@mui/icons-material/Event";
import RoomScheduleDialog from "./RoomScheduleDialog";
import CopyRoomScheduleDialog from "./CopyRoomScheduleDialog";


function formatEnsuite(value) {
  if (value === true) return "Yes";
  if (value === false) return "No";
  return "-";
}

function getRoomDisplayName(room) {
  return room.bedroomName || room.roomName || room.groupName || "-";
}

function normaliseCampusName(campus) {
  if (!campus) return "";
  if (typeof campus === "string") return campus.trim();
  return String(campus.name || campus.campusName || "").trim();
}

function toCampusOption(campus, index) {
  if (typeof campus === "string") {
    return { id: index, name: campus };
  }

  return {
    id: campus.id ?? index,
    name: campus.name || campus.campusName || "",
  };
}

function toDraft(room) {
  return {
    id: room.id,
    campus: room.campus || "",
    house: room.house || "",
    bedroomName: room.bedroomName || room.roomName || room.groupName || "",
    capacity: room.capacity ?? "",
    ensuite: room.ensuite === true,
    notes: room.notes || "",
  };
}

function RoomManagementView({
  rooms = [],
  selectedYear,
  search,
  selectedCampus,
  selectedHouse,
  onSearchChange,
  onSelectedCampusChange,
  onSelectedHouseChange,
  onCreateRoom,
  onEditRoom,
  onBulkEditRooms,
  onDeleteRoom,
  onSaveRoomSchedule,
}) {
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [activeCampuses, setActiveCampuses] = useState([]);
  const [editRows, setEditRows] = useState({});
  const [dirtyIds, setDirtyIds] = useState(new Set());
  const [scheduleRoom, setScheduleRoom] = useState(null);
  const [copyScheduleOpen, setCopyScheduleOpen] = useState(false);


  useEffect(() => {
    let cancelled = false;

    async function loadCampuses() {
      try {
        const loaded = await listActiveCampuses();
        if (!cancelled) {
          setActiveCampuses(Array.isArray(loaded) ? loaded : []);
        }
      } catch (error) {
        console.error("Failed to load active campuses", error);
        if (!cancelled) {
          setActiveCampuses([]);
        }
      }
    }

    loadCampuses();

    return () => {
      cancelled = true;
    };
  }, []);

  const campusOptions = useMemo(() => {
    const apiCampuses = (activeCampuses || [])
      .map((campus, index) => toCampusOption(campus, index))
      .filter((campus) => campus.name);

    const roomCampuses = (rooms || [])
      .map((room) => normaliseCampusName(room.campus))
      .filter(Boolean)
      .map((name, index) => ({ id: `room-${index}`, name }));

    const merged = [...apiCampuses, ...roomCampuses];

    const uniqueByName = [];
    const seen = new Set();

    for (const campus of merged) {
      const key = campus.name.trim().toLowerCase();
      if (!key || seen.has(key)) continue;
      seen.add(key);
      uniqueByName.push(campus);
    }

    return uniqueByName.sort((a, b) => a.name.localeCompare(b.name));
  }, [activeCampuses, rooms]);

  const houseOptions = useMemo(() => {
    return [
      ...new Set(
        (rooms || [])
          .filter(
            (room) =>
              !selectedCampus || normaliseCampusName(room.campus) === selectedCampus
          )
          .map((room) => String(room.house || "").trim())
          .filter(Boolean)
      ),
    ].sort((a, b) => a.localeCompare(b));
  }, [rooms, selectedCampus]);

  const filteredRooms = useMemo(() => {
    const q = String(search || "").trim().toLowerCase();

    return (rooms || []).filter((room) => {
      const campus = normaliseCampusName(room.campus);
      const house = String(room.house || "").trim();

      const matchesCampus = !selectedCampus || campus === selectedCampus;
      const matchesHouse = !selectedHouse || house === selectedHouse;

      const matchesSearch =
        !q ||
        [
          room.bedroomName,
          room.roomName,
          room.groupName,
          room.house,
          room.campus,
          room.notes,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(q);

      return matchesCampus && matchesHouse && matchesSearch;
    });
  }, [rooms, search, selectedCampus, selectedHouse]);

  const dirtyCount = dirtyIds.size;
  const hasAnyEdits = Object.keys(editRows).length > 0;
  const canSaveAll = dirtyCount > 0;

  const startEdit = (room) => {
    setEditRows((prev) => ({
      ...prev,
      [room.id]: prev[room.id] || toDraft(room),
    }));
  };

  const startEditAllRows = () => {
    setEditRows((prev) => {
      const next = { ...prev };
      filteredRooms.forEach((room) => {
        if (!next[room.id]) {
          next[room.id] = toDraft(room);
        }
      });
      return next;
    });
  };

  const cancelEdit = (roomId) => {
    setEditRows((prev) => {
      const next = { ...prev };
      delete next[roomId];
      return next;
    });

    setDirtyIds((prev) => {
      const next = new Set(prev);
      next.delete(roomId);
      return next;
    });
  };

  const cancelAllEdits = () => {
    const visibleIds = new Set(filteredRooms.map((room) => room.id));

    setEditRows((prev) => {
      const next = { ...prev };
      visibleIds.forEach((id) => delete next[id]);
      return next;
    });

    setDirtyIds((prev) => {
      const next = new Set(prev);
      visibleIds.forEach((id) => next.delete(id));
      return next;
    });
  };

  const markDirty = (roomId) => {
    setDirtyIds((prev) => {
      const next = new Set(prev);
      next.add(roomId);
      return next;
    });
  };

  const clearDirty = (roomId) => {
    setDirtyIds((prev) => {
      const next = new Set(prev);
      next.delete(roomId);
      return next;
    });
  };

  const changeField = (roomId, field, value) => {
    setEditRows((prev) => ({
      ...prev,
      [roomId]: {
        ...prev[roomId],
        [field]: value,
      },
    }));
    markDirty(roomId);
  };

  const changeBool = (roomId, field, value) => {
    setEditRows((prev) => ({
      ...prev,
      [roomId]: {
        ...prev[roomId],
        [field]: !!value,
      },
    }));
    markDirty(roomId);
  };

  const saveRow = async (room) => {
    const draft = editRows[room.id];
    if (!draft) return;

    await onEditRoom({
      ...room,
      ...draft,
      groupName: draft.bedroomName,
      roomName: draft.bedroomName,
    });

    setEditRows((prev) => {
      const next = { ...prev };
      delete next[room.id];
      return next;
    });

    clearDirty(room.id);
  };

  const saveAll = async () => {
    const roomsToSave = filteredRooms.filter((room) => dirtyIds.has(room.id));

    if (roomsToSave.length === 0) return;

    const payload = roomsToSave
      .map((room) => {
        const draft = editRows[room.id];
        if (!draft) return null;

        return {
          roomId: room.id,
          bedroomName: draft.bedroomName,
          campus: draft.campus,
          capacity: draft.capacity,
          notes: draft.notes,
          house: draft.house,
          ensuite: draft.ensuite,
        };
      })
      .filter(Boolean);

    await onBulkEditRooms(payload);

    const visibleIds = new Set(filteredRooms.map((room) => room.id));

    setEditRows((prev) => {
      const next = { ...prev };
      visibleIds.forEach((id) => delete next[id]);
      return next;
    });

    setDirtyIds((prev) => {
      const next = new Set(prev);
      visibleIds.forEach((id) => next.delete(id));
      return next;
    });
  };

  const handleDeleteClick = async (room) => {
    const count = Number(room.allocatedResidentCount || 0);

    const message =
      count > 0
        ? `This room has ${count} historic allocation${count === 1 ? "" : "s"}. Deleting it will only remove future scheduling after the last allocation end date. Continue?`
        : "Delete this room completely?";

    if (!window.confirm(message)) return;

    await onDeleteRoom(room.id);
  };

  const handleSaveSchedule = async (roomId, dates) => {
    await onSaveRoomSchedule(roomId, selectedYear, dates);
    setScheduleRoom(null);
  };

  const handleCopySchedule = async ({ sourceRoomId, targetRoomIds }) => {
  const sourceRoom = rooms.find((room) => String(room.id) === String(sourceRoomId));
  if (!sourceRoom) return;

  const sourceDates = Array.isArray(sourceRoom.scheduledDates)
    ? sourceRoom.scheduledDates
    : [];

  for (const targetRoomId of targetRoomIds) {
    await onSaveRoomSchedule(targetRoomId, selectedYear, sourceDates);
  }

  setCopyScheduleOpen(false);
};


  return (
    <Box>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", md: "center" }}
        sx={{ mb: 2 }}
      >
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Room Management
        </Typography>

        <Stack direction="row" spacing={1} flexWrap="wrap">
          {!hasAnyEdits ? (
            <Button variant="outlined" onClick={startEditAllRows}>
              Edit all
            </Button>
          ) : (
            <>
              <Button variant="outlined" onClick={cancelAllEdits}>
                Cancel all
              </Button>
              <Button variant="contained" onClick={saveAll} disabled={!canSaveAll}>
                Save all{canSaveAll ? ` (${dirtyCount})` : ""}
              </Button>
            </>
          )}

          <Button variant="contained" onClick={() => setCreateDialogOpen(true)}>
            Create Room
          </Button>
          <Button variant="outlined" onClick={() => setCopyScheduleOpen(true)}>
            Copy Schedule
          </Button>

        </Stack>
      </Stack>

      <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 2 }}>
        <TextField
          size="small"
          variant="outlined"
          label="Search rooms"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          sx={{
            minWidth: 180,
            "& .MuiOutlinedInput-root": {
              height: 40,
            },
          }}
        />

        <FormControl size="small" variant="outlined" sx={{ minWidth: 180 }}>
          <InputLabel>Campus</InputLabel>
          <Select
            value={selectedCampus}
            label="Campus"
            onChange={(e) => {
              onSelectedCampusChange(e.target.value);
              onSelectedHouseChange("");
            }}
            sx={{
              minWidth: 220,
              "& .MuiOutlinedInput-root": { height: 40 },
             }}
          >
            <MenuItem value="">All Campuses</MenuItem>
            {campusOptions.map((campus) => (
              <MenuItem key={campus.id} value={campus.name}>
                {campus.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" variant="outlined" sx={{ minWidth: 180 }}>
          <InputLabel>House</InputLabel>
          <Select
            value={selectedHouse}
            label="House"
            onChange={(e) => onSelectedHouseChange(e.target.value)}
            sx={{
              minWidth: 220,
              "& .MuiOutlinedInput-root": { height: 40 },
            }}
          >
            <MenuItem value="">All Houses</MenuItem>
            {houseOptions.map((house) => (
              <MenuItem key={house} value={house}>
                {house}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Campus</TableCell>
              <TableCell>House</TableCell>
              <TableCell>Room</TableCell>
              <TableCell>Capacity</TableCell>
              <TableCell>Ensuite</TableCell>
              <TableCell>Allocations</TableCell>
              <TableCell>Scheduled</TableCell>
              <TableCell>Notes</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {filteredRooms.map((room) => {
              const draft = editRows[room.id];
              const isEditing = !!draft;

              return (
                <TableRow key={room.id} hover>
                  <TableCell sx={{ minWidth: 180 }}>
                    {isEditing ? (
                      <FormControl size="small" fullWidth variant="standard">
                        <Select
                          value={draft.campus}
                          onChange={(e) => changeField(room.id, "campus", e.target.value)}
                        >
                          {campusOptions.map((campus) => (
                            <MenuItem key={campus.id} value={campus.name}>
                              {campus.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      room.campus || "-"
                    )}
                  </TableCell>

                  <TableCell sx={{ minWidth: 160 }}>
                    {isEditing ? (
                      <TextField
                        variant="standard"
                        fullWidth
                        value={draft.house}
                        onChange={(e) => changeField(room.id, "house", e.target.value)}
                      />
                    ) : (
                      room.house || "-"
                    )}
                  </TableCell>

                  <TableCell sx={{ minWidth: 180 }}>
                    {isEditing ? (
                      <TextField
                        variant="standard"
                        fullWidth
                        value={draft.bedroomName}
                        onChange={(e) => changeField(room.id, "bedroomName", e.target.value)}
                      />
                    ) : (
                      getRoomDisplayName(room)
                    )}
                  </TableCell>

                  <TableCell sx={{ minWidth: 100 }}>
                    {isEditing ? (
                      <TextField
                        variant="standard"
                        type="number"
                        fullWidth
                        value={draft.capacity}
                        onChange={(e) => changeField(room.id, "capacity", e.target.value)}
                      />
                    ) : (
                      room.capacity ?? "-"
                    )}
                  </TableCell>

                  <TableCell align="center" sx={{ minWidth: 110 }}>
                    {isEditing ? (
                      <Checkbox
                        checked={!!draft.ensuite}
                        onChange={(e) => changeBool(room.id, "ensuite", e.target.checked)}
                        size="small"
                      />
                    ) : (
                      formatEnsuite(room.ensuite)
                    )}
                  </TableCell>

                  <TableCell>{room.allocatedResidentCount ?? 0}</TableCell>
                  <TableCell>{room.scheduledDayCount ?? 0} days</TableCell>

                  <TableCell sx={{ minWidth: 220 }}>
                    {isEditing ? (
                      <TextField
                        variant="standard"
                        fullWidth
                        value={draft.notes}
                        onChange={(e) => changeField(room.id, "notes", e.target.value)}
                      />
                    ) : (
                      room.notes || "-"
                    )}
                  </TableCell>

                  <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                    {isEditing ? (
                      <>
                        <Button size="small" onClick={() => saveRow(room)} sx={{ mr: 1 }}>
                          Save
                        </Button>
                        <Button size="small" onClick={() => cancelEdit(room.id)}>
                          Cancel
                        </Button>
                      </>
                    ) : (
                      <>
                        <IconButton onClick={() => startEdit(room)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton onClick={() => setScheduleRoom(room)}>
                          <EventIcon fontSize="small" />
                        </IconButton>
                        <IconButton onClick={() => handleDeleteClick(room)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}

            {filteredRooms.length === 0 && (
              <TableRow>
                <TableCell colSpan={8}>
                  <Typography variant="body2" color="text.secondary">
                    No rooms found.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {canSaveAll && (
        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            zIndex: 10,
            mt: 2,
            py: 1,
            px: 2,
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 1,
            bgcolor: "background.paper",
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography variant="body2" sx={{ mr: "auto" }}>
            {dirtyCount} unsaved change{dirtyCount === 1 ? "" : "s"}
          </Typography>

          <Button variant="contained" onClick={saveAll}>
            Save all changes ({dirtyCount})
          </Button>
        </Box>
      )}

      <AddBedroomDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        selectedCampus={selectedCampus || ""}
        campuses={campusOptions}
        selectedHouse={selectedHouse || ""}
        onSave={async (bedroom) => {
          await onCreateRoom(bedroom);
          setCreateDialogOpen(false);
        }}
      />
      {scheduleRoom && (
        <RoomScheduleDialog
          open={Boolean(scheduleRoom)}
          room={scheduleRoom}
          year={selectedYear}
          onClose={() => setScheduleRoom(null)}
          onSave={handleSaveSchedule}
        />
      )}

      <CopyRoomScheduleDialog
        open={copyScheduleOpen}
        onClose={() => setCopyScheduleOpen(false)}
        rooms={filteredRooms}
        onSave={handleCopySchedule}
      />

    </Box>
  );
}

export default RoomManagementView;